import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Menu,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  Tabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  cancelOrder,
  getOrderReport,
  markOrderReadyForPickup,
  refundOrder,
} from 'api/orderManagement'
import {
  Link,
  Meta,
  ModalConfig,
  ModalType,
  OrderReportResponse,
  PaymentProvider,
  ReportingOrder,
} from 'api/orderManagement/types'
import ClearableSearchBar from 'components/ClearableSearchBar/ClearableSearchBar'
import LoadingSkeleton from 'components/LoadingSkeleton/LoadingSkeleton'
import MultiLocationDropdown from 'components/MultiLocationDropdown/MultiLocationDropdown'
import Truncated from 'components/Truncated/Truncated'
import { useGetLocations } from 'hooks'
import { useAuth0 } from 'libs/auth0-react'
import { debounce, startCase } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { fontFamily } from 'theme'
import { Location, PlatformName } from 'types'
import { formatSubmissionDate, formatSubmissionTime, getOrderRawPayloadURL } from 'utils'
import { v4 as uuidv4 } from 'uuid'

import { DropdownFilter, FilterOption } from '../../components/DropdownFilter/DropdownFilter'
import useLocationOrderPlatforms from '../../hooks/useLocationOrderPlatforms'
import OrderModal from './Modals/OrderModal'
import RefundModal from './Modals/RefundModal'
import OrderDetailsDrawer from './OrderDetailsDrawer'
import * as Styled from './OrderManagementStyles'
import { Toast } from 'components'

export enum OrderDisplayStatus {
  ALL = '',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  ERRORED = 'errored',
}

export const statusConfig: Record<OrderDisplayStatus, { color: string; icon: JSX.Element }> = {
  [OrderDisplayStatus.COMPLETED]: { color: 'green', icon: <CheckIcon fontSize='small' /> },
  [OrderDisplayStatus.CANCELLED]: { color: 'red', icon: <CloseIcon fontSize='small' /> },
  [OrderDisplayStatus.ERRORED]: { color: 'red', icon: <ErrorOutlineIcon fontSize='small' /> },
  [OrderDisplayStatus.ALL]: { color: 'gray', icon: <></> },
}

export const mapOrderStatus = (status: string): OrderDisplayStatus => {
  switch (status) {
    case 'completed':
      return OrderDisplayStatus.COMPLETED
    case 'cancelled':
      return OrderDisplayStatus.CANCELLED
    case 'errored':
    case 'error':
    case 'failed':
    case 'erred':
    case 'payment_erred':
      return OrderDisplayStatus.ERRORED
    default:
      return OrderDisplayStatus.ALL
  }
}

const ALL_AASM_STATES = [
  'cancellation_failed',
  'cancelled',
  'erred',
  'finalized',
  'payment_captured',
  'payment_erred',
  'posted',
  'replaced',
] as const

type AasmState = (typeof ALL_AASM_STATES)[number]

export const OrderManagement = () => {
  const { defaultLocation, isAdminUser, locations } = useGetLocations()
  const [selectedLocations, setSelectedLocations] = useState<Location[]>(
    defaultLocation
      ? [
          {
            id: defaultLocation.id.toString(),
            name: defaultLocation.name,
            organization_id: defaultLocation.organization_id,
            organization_name: defaultLocation.organization_name,
            timezone: defaultLocation.timezone,
          },
        ]
      : [],
  )
  const [searchByName, setSearchByName] = useState<string>('')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()

  // Tracks if the component has loaded from the URL to prevent re-running logic unnecessarily
  const [hasLoadedFromUrl, setHasLoadedFromUrl] = useState(false)

  useEffect(() => {
    // Get the 'partnerIds' parameter from the URL, if available
    const searchParams = new URLSearchParams(location.search)
    const urlPartnerIds = searchParams.get('partnerIds')

    // Only proceed if the page hasn't been loaded from the URL yet and locations are available
    if (!hasLoadedFromUrl && locations.length > 0) {
      // If partnerIds are available in the URL, process them
      if (urlPartnerIds) {
        // Convert the 'partnerIds' string into an array of trimmed strings
        const partnerIdArray = urlPartnerIds.split(',').map((id) => id.trim())
        // Filter the locations to match those whose ids are in the partnerIdArray
        const matchingLocations: Location[] = locations
          .filter((loc) => partnerIdArray.includes(loc.id.toString()))
          .map((loc) => ({
            ...loc,
            id: loc.id.toString(),
          }))

        if (matchingLocations.length > 0) {
          setSelectedLocations(matchingLocations)
        }
      } else if (defaultLocation && selectedLocations.length === 0 && isAdminUser) {
        const defaultLoc = {
          id: defaultLocation.id.toString(),
          name: defaultLocation.name,
          organization_id: defaultLocation.organization_id,
          organization_name: defaultLocation.organization_name,
          timezone: defaultLocation.timezone,
        }

        // Set the default location as the selected one
        setSelectedLocations([defaultLoc])

        // Update the URL to include the default location's ID in the 'partnerIds' parameter
        searchParams.set('partnerIds', defaultLoc.id)
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
      }

      setHasLoadedFromUrl(true)
    }
  }, [defaultLocation, locations, location.search, location.pathname, navigate, hasLoadedFromUrl])

  // Handle location change from the dropdown
  const handleLocationChange = (locations: Location[]) => {
    const selectedIds = locations.map((loc) => loc.id.toString())
    const currentIds = selectedLocations.map((loc) => loc.id.toString())

    if (JSON.stringify(selectedIds) === JSON.stringify(currentIds)) return

    setSelectedLocations(locations)
  }

  // Updates the URL with the selected locations' partnerIds, preserving other query params
  useEffect(() => {
    if (!hasLoadedFromUrl) return

    const currentParams = new URLSearchParams(location.search)
    currentParams.delete('partnerIds')

    let query = ''

    if (selectedLocations.length > 0) {
      const selectedIds = selectedLocations.map((loc) => loc.id.toString()).join(',')
      const otherParams = currentParams.toString()
      query = `partnerIds=${selectedIds}${otherParams ? `&${otherParams}` : ''}`
    } else {
      query = currentParams.toString()
    }

    navigate(`${location.pathname}?${query}`, { replace: true })
  }, [selectedLocations, location.pathname, location.search, navigate, hasLoadedFromUrl])

  return (
    <Styled.Container>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: isMobile ? '16px' : '10px',
          marginBottom: '16px',
        }}
      >
        <div style={{ width: isMobile ? '100%' : '50%' }}>
          <MultiLocationDropdown
            onLocationChange={handleLocationChange}
            selectedLocations={selectedLocations}
            checkDefaultAll={!isAdminUser ? true : false}
          />
        </div>
        <div style={{ width: isMobile ? '100%' : '50%', paddingLeft: isMobile ? '0' : '100px' }}>
          <ClearableSearchBar
            onSearch={(value) => {
              setSearchByName(value)
            }}
            buttonText='Search Orders'
            placeholder='Search by Customer Name'
            initialValue={searchByName}
          />
        </div>
      </div>
      <TableComponent selectedLocations={selectedLocations} searchByName={searchByName} />
    </Styled.Container>
  )
}

export const TableComponent = ({
  selectedLocations,
  searchByName,
}: {
  selectedLocations: Location[]
  searchByName: string
}) => {
  const { user } = useAuth0()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isAdminUser = user?.email?.includes('@chowlyinc.com')

  // Get location IDs and partnerId for API calls
  const locationIds = useMemo(() => {
    return selectedLocations.map((location) => location.id.toString()) || []
  }, [selectedLocations])

  const partnerId = locationIds.join(',')

  const [statusTab, setStatusTab] = useState<OrderDisplayStatus>(OrderDisplayStatus.ALL)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [page, setPage] = useState(0)
  const [data, setData] = useState<ReportingOrder[]>([])
  const [meta, setMeta] = useState<Meta>({
    limit: `${rowsPerPage}`,
    page: 1,
    page_total: 1,
    total: 0,
  })

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [link, setLink] = useState<Link>({
    first: null,
    prev: null,
    self: null,
    next: null,
    last: null,
  })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const [selectedModalOrder, setSelectedModalOrder] = useState<ReportingOrder | null>(null)
  const [modalType, setModalType] = useState<keyof typeof modalConfigMap | ''>('')
  const {
    data: platforms,
    locationPartnerIds,
    addLocationPartners,
    removeLocationPartners,
  } = useLocationOrderPlatforms()

  const platformOptions: FilterOption[] = platforms.map((platform) => ({
    label: platform.name,
    value: platform.name,
    isAllOption: platform.name === 'All Platforms',
  }))

  const orderTypeOptions: FilterOption[] = [
    { label: 'All Types', value: '', isAllOption: true },
    { label: 'Pick Up', value: 'Pick-Up' },
    { label: 'Delivery', value: 'Delivery' },
  ]
  const defaultSelectedOrderType = orderTypeOptions[0]
  const defaultSelectedPlatform = platformOptions[0]
  const [selectedPlatforms, setSelectedPlatforms] = useState<FilterOption[]>([
    defaultSelectedPlatform,
  ])
  const [selectedOrderType, setSelectedOrderType] = useState<FilterOption>(defaultSelectedOrderType)
  const activeRequestId = useRef<string | null>(null) // Create a ref to track the active request
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [selectedAasmStates, setSelectedAasmStates] = useState<FilterOption[]>([])

  const aasmStateOptions = useMemo(() => {
    const options: FilterOption[] = [
      {
        label: 'All States',
        value: 'all',
        isAllOption: true,
      },
    ]

    ALL_AASM_STATES.forEach((state: AasmState) => {
      options.push({
        label: startCase(state),
        value: state,
        isAllOption: false,
      })
    })

    return options
  }, [])

  const handleAasmStateChange = (options: FilterOption[]) => {
    // If "All States" is selected, clear other selections
    if (options.some((option) => option.isAllOption)) {
      setSelectedAasmStates([aasmStateOptions[0]])
      return
    }

    // If selecting a specific state, remove "All States" if it was selected
    const filteredOptions = options.filter((option) => !option.isAllOption)
    setSelectedAasmStates(filteredOptions)
  }

  useEffect(() => {
    if (isAdminUser && selectedLocations.length > 0) {
      // Reset to "All States" if no AASM states are selected
      if (selectedAasmStates.length === 0) {
        setSelectedAasmStates([aasmStateOptions[0]])
      }
    }
  }, [isAdminUser, selectedLocations, selectedAasmStates, aasmStateOptions])

  const setFilter = () => {
    const filter: {
      status?: string
      platforms?: string[] | string
      order_type?: string[] | string
      customer_name?: string
      aasm_state?: string[] | string
    } = {}
    if (statusTab) {
      filter.status = statusTab
    }
    if (
      selectedPlatforms.length > 0 &&
      selectedPlatforms[0].value !== defaultSelectedPlatform.value
    ) {
      filter.platforms =
        selectedPlatforms.length === 1
          ? selectedPlatforms[0].value
          : selectedPlatforms.map((p) => p.value)
    }
    if (selectedOrderType !== defaultSelectedOrderType && selectedOrderType.value !== '') {
      const selectedOption = orderTypeOptions.find(
        (option) => option.value === selectedOrderType.value,
      )
      filter.order_type = selectedOption?.value || selectedOrderType.value
    }

    // search by customer name
    if (searchByName) {
      filter.customer_name = searchByName
    }

    // Add AASM states filter for admin users
    if (
      isAdminUser &&
      selectedAasmStates.length > 0 &&
      !selectedAasmStates.some((state: FilterOption) => state.value === 'all')
    ) {
      filter.aasm_state = selectedAasmStates.map((state: FilterOption) => state.value)
    }

    return filter
  }

  const modalConfigMap: Record<string, ModalConfig> = {
    cancel: {
      title: (orderId: string) => `Cancel Order #${orderId}`,
      description: 'Are you sure you want to cancel this order? This action cannot be undone.',
      submitText: 'Cancel Order',
      action: cancelOrder,
    },
    refund: {
      title: (orderId: string) => `Refund Order #${orderId}`,
      description:
        'Are you sure you want to refund this order? Once refunded, this action cannot be undone.',
      submitText: 'Refund Order',
      action: refundOrder,
    },
    ready_for_pickup: {
      title: (orderId: string) => `Mark Order ${orderId ? orderId : ''} Ready for Pickup`,
      description: 'Are you sure this order is ready for pickup?',
      submitText: `Yes, It's Ready!`,
      action: async (orderId: number) => {
        const response = await markOrderReadyForPickup(orderId)
        return response
      },
    },
  }

  const getPartnerName = (partnerId: string | null): string => {
    if (partnerId === null) {
      return 'Unknown Location'
    }

    const location = selectedLocations.find((loc) => loc.id === partnerId)
    return location ? location.name : 'Unknown Location'
  }

  const handleModalOpen = (order: ReportingOrder, type: keyof typeof modalConfigMap, mapOrderId?: string) => {
    let selectedOrder = order
    if (mapOrderId) {
      const foundOrder = data.find((o) => String(o.attributes.internal_order_id) === mapOrderId)
      if (!foundOrder) return
      selectedOrder = foundOrder
    }
    setSelectedModalOrder(selectedOrder)
    
    if (type === 'refund') {
      setIsRefundModalOpen(true)
    } else {
      setModalType(type)
      setIsModalOpen(true)
    }
  }


  const handleModalClose = (
    _event: Record<string, never>,
    _reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    setIsModalOpen(false)
    setIsRefundModalOpen(false)
    setSelectedModalOrder(null)
    setModalType('')
  }

  const handleRefundSubmit = async (isFullRefund: boolean, refundAmount?: number) => {
    try {
      if (selectedModalOrder) {
        await refundOrder(
          selectedModalOrder.attributes.internal_order_id,
          isFullRefund,
          refundAmount,
        )
        fetchOrderReport()
      }
    } catch (error) {
      console.error('Error processing refund:', error)
    }
  }

  const handleModalAction = async (orderId: number, type: ModalType) => {
    try {
      if (type === ModalType.ready_for_pickup) {
        const response = await modalConfigMap[type].action(orderId)
        if (response?.ready_at) {
          if (selectedOrder?.attributes.internal_order_id === orderId) {
            setSelectedOrder((prevOrder) => {
              if (prevOrder) {
                return {
                  ...prevOrder,
                  attributes: {
                    ...prevOrder.attributes,
                    ready_at: response.ready_at,
                  },
                }
              }
              return prevOrder
            })
          }
        }
      } else if (type !== ModalType.refund) {
        await modalConfigMap[type].action(orderId)
        fetchOrderReport()
      }
      setIsModalOpen(false)
    } catch (error) {
      console.error('Error handling modal submit:', error)
    }
  }

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<ReportingOrder | null>(null)

  const fetchOrderReport = useCallback(
    debounce(async (link: string | null = null) => {
      if (!selectedLocations || selectedLocations.length === 0) {
        setData([]) // Clean previous data when location is empty
        return
      }

      setIsLoading(true)
      const requestId = uuidv4()
      activeRequestId.current = requestId // Set the active request ID
      if (requestId !== activeRequestId.current) return

      try {
        setIsLoading(true)
        const response: OrderReportResponse = await getOrderReport({
          partnerId,
          link,
          limit: rowsPerPage,
          filter: setFilter(),
        })

        setLink(response?.links)
        setData(response?.data)
        setMeta(response?.meta)
      } catch (error) {
        console.error('Error fetching order report:', error)
      } finally {
        setIsLoading(false)
      }
    }, 300),
    [
      selectedLocations,
      statusTab,
      rowsPerPage,
      selectedPlatforms,
      selectedOrderType,
      searchByName,
      selectedAasmStates,
      partnerId,
    ],
  )

  useEffect(() => {
    const locationIdsToAdd = locationIds.filter(
      (location) => !locationPartnerIds.includes(location),
    )

    if (!locationIdsToAdd.length) return

    addLocationPartners(locationIdsToAdd)
  }, [locationIds])

  useEffect(() => {
    const locationIdsToRemove = locationPartnerIds.filter(
      (location) => !locationIds.includes(location),
    )
    if (!locationIdsToRemove.length) return

    removeLocationPartners(locationIdsToRemove)
  }, [locationIds])

  useEffect(() => {
    setPage(0)
  }, [selectedLocations])

  useEffect(() => {
    fetchOrderReport()
  }, [
    statusTab,
    rowsPerPage,
    selectedLocations,
    fetchOrderReport,
    selectedPlatforms,
    selectedOrderType,
    searchByName,
    selectedAasmStates,
  ])

  const handleTabChange = (event: React.SyntheticEvent, newValue: OrderDisplayStatus) => {
    setStatusTab(newValue)
    setPage(0)
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, rowIndex: number) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
    setSelectedRow(rowIndex)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedRow(null)
  }

  const handleRowClick = (_event: React.MouseEvent<HTMLTableRowElement>, order: ReportingOrder) => {
    setSelectedOrder(order)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false)
    setSelectedOrder(null)
  }

  const handleChangePage = (event: any, newPage: number) => {
    if (isLoading || newPage === page) {
      return
    }

    if (newPage > page && !link?.next) {
      return
    }

    if (newPage < page && !link?.prev) {
      return
    }

    setIsLoading(true)

    setPage(newPage)

    if (newPage > page && link?.next) {
      fetchOrderReport(link?.next)
    } else if (newPage < page && link?.prev) {
      fetchOrderReport(link?.prev)
    } else if (newPage === page && link?.self) {
      fetchOrderReport(link?.self)
    }
  }

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <>
      <Styled.FiltersContainer
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
        }}
      >
        {selectedLocations.length > 0 && (
          <Tabs
            value={statusTab}
            onChange={handleTabChange}
            TabIndicatorProps={{ style: { display: 'none' } }}
            textColor='primary'
            sx={{
              minHeight: 'unset',
              '& .MuiTabs-root': {
                minHeight: 'unset',
              },
            }}
          >
            <Styled.StyledTab
              label='All'
              value={OrderDisplayStatus.ALL}
              active={statusTab === OrderDisplayStatus.ALL}
            />
            <Styled.StyledTab
              label='Completed'
              value={OrderDisplayStatus.COMPLETED}
              active={statusTab === OrderDisplayStatus.COMPLETED}
            />
            <Styled.StyledTab
              label='Cancelled'
              value={OrderDisplayStatus.CANCELLED}
              active={statusTab === OrderDisplayStatus.CANCELLED}
            />
            <Styled.StyledTab
              label='Errored'
              value={OrderDisplayStatus.ERRORED}
              active={statusTab === OrderDisplayStatus.ERRORED}
            />
          </Tabs>
        )}

        {isAdminUser && (
          <div
            style={{
              marginLeft: isMobile ? '0' : 'auto',
              marginTop: isMobile ? '16px' : '0',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <DropdownFilter
              label='AASM Order State'
              selectedOptions={selectedAasmStates}
              onValueChange={handleAasmStateChange}
              options={aasmStateOptions}
              multiple={true}
            />
          </div>
        )}

        <div
          style={{
            marginLeft: isMobile ? '0' : isAdminUser ? '32px' : 'auto',
            marginTop: isMobile && isAdminUser ? '16px' : '0',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <DropdownFilter
            label='Order Type'
            selectedOptions={[selectedOrderType]}
            onValueChange={(option: FilterOption[]) => setSelectedOrderType(option[0])}
            options={orderTypeOptions}
            data-testid='order-type-filter'
          />
        </div>
        <div
          style={{
            marginLeft: isMobile ? '0' : '32px',
            marginTop: isMobile ? '16px' : '0',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <DropdownFilter
            label='Platform'
            selectedOptions={selectedPlatforms}
            onValueChange={setSelectedPlatforms}
            options={platformOptions}
            multiple
            data-testid='platform-filter'
          />
        </div>
      </Styled.FiltersContainer>
      {isLoading ? (
        <LoadingSkeleton lineCount={3} />
      ) : (
        <Styled.EmptyStateWrapper>
          <Styled.Container>
            {data.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <Styled.StyledTableRow $isHeader>
                      <Styled.MobileVisibleTableHeader>
                        Platform Ticket ID
                      </Styled.MobileVisibleTableHeader>
                      <Styled.CustomTableCell>Status</Styled.CustomTableCell>
                      {isAdminUser && (
                        <Styled.CustomTableCell>AASM State</Styled.CustomTableCell>
                      )}
                      <Styled.CustomTableCell>Platform</Styled.CustomTableCell>
                      <Styled.CustomTableCell>Location</Styled.CustomTableCell>
                      <Styled.MobileVisibleTableHeader>
                        Submission Time
                      </Styled.MobileVisibleTableHeader>
                      {/* <Styled.CustomTableCell>POS Ticket ID </Styled.CustomTableCell> */}
                      <Styled.MobileVisibleTableHeader>
                        Customer Name
                      </Styled.MobileVisibleTableHeader>
                      <Styled.CustomTableCell>Delivery Address</Styled.CustomTableCell>
                      <Styled.CustomTableCell>Order Type</Styled.CustomTableCell>
                      <Styled.CustomTableCell>Actions</Styled.CustomTableCell>
                    </Styled.StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row, index) => {
                      const {
                        order_platform_order_id,
                        order_status,
                        platform_name,
                        submission_time,
                        // pos_order_id,
                        customer_first_name,
                        customer_last_name,
                        delivery_street,
                        internal_order_id,
                        partner_id,
                        order_type,
                      } = row.attributes

                      const [streetAddress, ...remainingAddress] = delivery_street.split(',')

                      return (
                        <Styled.StyledTableRow
                          key={row.id}
                          onClick={(event) => handleRowClick(event, row)}
                        >
                          <Styled.MobileVisibleTableCell>
                            {order_platform_order_id ? (
                              <Truncated text={order_platform_order_id} />
                            ) : null}
                          </Styled.MobileVisibleTableCell>

                          <Styled.CustomTableCell>
                            {statusConfig[mapOrderStatus(order_status)] && (
                              <Styled.StyledChip
                                label={mapOrderStatus(order_status)}
                                icon={
                                  statusConfig[mapOrderStatus(order_status) as OrderDisplayStatus]
                                    .icon
                                }
                                status={mapOrderStatus(order_status)}
                                size='small'
                              />
                            )}
                          </Styled.CustomTableCell>

                          {isAdminUser && (
                            <Styled.CustomTableCell>
                              {row.attributes.aasm_state ? startCase(row.attributes.aasm_state) : 'N/A'}
                            </Styled.CustomTableCell>
                          )}

                          {isAdminUser ? (
                            <Styled.CustomTableCell>
                              <Tooltip title="Open JSON Payload" arrow>
                                <Styled.CustomLink
                                  data-testid="platform-payload-link"
                                  onClick={() => {
                                    if (!internal_order_id) {
                                      Toast.error({ message: 'Unable to open order payload. Please try again.' }, { autoClose: 5000 });
                                      return;
                                    }
                                    window.open(getOrderRawPayloadURL(String(internal_order_id)), '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  {platform_name}
                                </Styled.CustomLink>
                              </Tooltip>
                            </Styled.CustomTableCell>
                          ) : (
                            <Styled.CustomTableCell>{platform_name}</Styled.CustomTableCell>
                          )}

                          <Styled.CustomTableCell>
                            <Styled.CustomTooltip
                              title={getPartnerName(String(partner_id))}
                              arrow
                              placement='top'
                            >
                              <Styled.LocationText>
                                {getPartnerName(String(partner_id))}
                              </Styled.LocationText>
                            </Styled.CustomTooltip>
                          </Styled.CustomTableCell>

                          <Styled.MobileVisibleTableCell>
                            <Styled.SubmissionTime>
                              {formatSubmissionTime(submission_time)}
                            </Styled.SubmissionTime>
                            <Styled.SubmissionDate variant='body2'>
                              {formatSubmissionDate(submission_time)}
                            </Styled.SubmissionDate>
                          </Styled.MobileVisibleTableCell>

                          {/* <Styled.CustomTableCell>{pos_order_id}</Styled.CustomTableCell> */}

                          <Styled.MobileVisibleTableCell>
                            {`${customer_first_name} ${customer_last_name}`}
                          </Styled.MobileVisibleTableCell>

                          <Styled.CustomTableCell>
                            {streetAddress.length ? (
                              <Styled.Address>{streetAddress}</Styled.Address>
                            ) : (
                              <Styled.EmptyAddress>N/A</Styled.EmptyAddress>
                            )}
                            {remainingAddress.length ? (
                              <Styled.Address>{remainingAddress}</Styled.Address>
                            ) : null}
                          </Styled.CustomTableCell>

                          <Styled.CustomTableCell>{order_type || 'N/A'}</Styled.CustomTableCell>

                          {row.attributes.platform_name === PlatformName.COO && (
                            <Styled.CenterTableCell>
                              <Styled.CenteredIconButton
                                onClick={(e) => handleMenuOpen(e, index)}
                                data-testid='more-button'
                              >
                                <MoreHorizIcon sx={{ fontSize: 13 }} />
                              </Styled.CenteredIconButton>
                              <Menu
                                anchorEl={anchorEl}
                                open={selectedRow === index}
                                onClose={handleMenuClose}
                              >
                                <Styled.MenuItems
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      handleModalOpen(row, 'cancel')
                                      handleMenuClose()
                                    }}
                                    disabled={order_status === OrderDisplayStatus.COMPLETED}
                                  >
                                    Cancel Order
                                  </Styled.MenuItems>
                                {row.attributes.payment_provider === PaymentProvider.stripe && (
                                  <Styled.MenuItems
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      handleModalOpen(row, 'refund')
                                      handleMenuClose()
                                    }}
                                  >
                                    Refund Order
                                  </Styled.MenuItems>
                                )}
                              </Menu>
                            </Styled.CenterTableCell>
                          )}
                        </Styled.StyledTableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Styled.EmptyStateText>No Orders Available</Styled.EmptyStateText>
            )}

            <Styled.PaginationContainer>
              <TablePagination
                component='div'
                count={meta?.total || 0}
                page={page}
                onPageChange={(event, newPage) => {
                  handleChangePage(event, newPage)
                }}
                slotProps={{
                  actions: {
                    previousButton: {
                      disabled: page === 0 || isLoading,
                    },
                    nextButton: {
                      disabled: page === meta?.page_total - 1 || isLoading,
                    },
                  },
                }}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage='Rows per page'
                rowsPerPageOptions={[5, 10, 15, 20, 25]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-caption, & .MuiTablePagination-input, & .MuiTablePagination-select':
                    {
                      fontFamily: fontFamily.inter,
                      fontSize: 13,
                    },
                }}
              />
            </Styled.PaginationContainer>

            <OrderDetailsDrawer
              isOpen={isDrawerOpen}
              onClose={handleCloseDrawer}
              internalOrderId={selectedOrder?.attributes.internal_order_id || null}
              onModalOpen={handleModalOpen}
            />

            {selectedModalOrder && modalType && (
              <OrderModal
                isOpen={isModalOpen}
                title={modalConfigMap[modalType].title(
                  selectedModalOrder.attributes.order_platform_order_id,
                )}
                description={modalConfigMap[modalType].description}
                submitText={modalConfigMap[modalType].submitText}
                onClose={handleModalClose}
                onSubmit={(_event) =>
                  handleModalAction(
                    selectedModalOrder.attributes.internal_order_id,
                    modalType as ModalType,
                  )
                }
                order={selectedModalOrder as unknown as ReportingOrder}
              />
            )}

            {selectedModalOrder && (
              <RefundModal
                isOpen={isRefundModalOpen}
                onClose={handleModalClose}
                onSubmit={handleRefundSubmit}
                order={selectedModalOrder}
              />
            )}
          </Styled.Container>
        </Styled.EmptyStateWrapper>
      )}
    </>
  )
}

export default OrderManagement
